import { useState } from 'react';
import { FileText, Link2, Download, X } from 'lucide-react';
import { useTaskFiles, useUnlinkFile } from '../../hooks/queries/useTaskFiles';
import { getDownloadUrl } from '../../services/files/filesService';
import { formatFileSize } from '../../services/files/fileUtils';
import { LinkFileModal } from './LinkFileModal';
import type { DossierFile } from '../../services/files/filesService';

interface Props {
  taskId: string;
  dossierId: string;
}

export function TaskDossierFilesSection({ taskId, dossierId }: Props) {
  const { data: files = [] } = useTaskFiles(taskId);
  const unlink = useUnlinkFile(taskId);
  const [linkOpen, setLinkOpen] = useState(false);

  async function handleDownload(file: DossierFile) {
    try {
      const url = await getDownloadUrl(file.id);
      window.open(url, '_blank');
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Erreur');
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-neutral-700">Fichiers du dossier</h3>
        <button
          type="button"
          onClick={() => setLinkOpen(true)}
          className="flex items-center gap-1.5 text-xs text-cime-primary hover:underline"
        >
          <Link2 className="w-3.5 h-3.5" /> Joindre un fichier
        </button>
      </div>

      {files.length === 0 ? (
        <p className="text-xs text-neutral-400">Aucun fichier du dossier lié à cette tâche.</p>
      ) : (
        <div className="space-y-1">
          {files.map((file) => (
            <div
              key={file.id}
              className="flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-neutral-200 bg-neutral-50"
            >
              <FileText className="w-4 h-4 text-neutral-500 flex-shrink-0" />
              <span className="flex-1 min-w-0 text-sm text-neutral-800 truncate">{file.filename}</span>
              <span className="text-xs text-neutral-400">{formatFileSize(file.size)}</span>
              <button
                type="button"
                onClick={() => handleDownload(file)}
                className="p-1 rounded hover:bg-neutral-200"
                aria-label="Télécharger"
              >
                <Download className="w-3.5 h-3.5 text-neutral-600" />
              </button>
              <button
                type="button"
                onClick={() => unlink.mutate(file.id)}
                className="p-1 rounded hover:bg-neutral-200"
                aria-label="Détacher"
              >
                <X className="w-3.5 h-3.5 text-neutral-600" />
              </button>
            </div>
          ))}
        </div>
      )}

      {linkOpen && (
        <LinkFileModal
          taskId={taskId}
          dossierId={dossierId}
          alreadyLinkedIds={files.map((f) => f.id)}
          onClose={() => setLinkOpen(false)}
        />
      )}
    </div>
  );
}
